/**
 * Utility functions for transforming unit installments data
 * into formats needed by various screens
 */

import type { Property } from '@/core/api/models/profile';
import type { InstallmentItem, UnitInstallmentsMap, UnitInstallmentsResponse } from '@/core/api/models/unitInstallments';

/**
 * Installment item with property information
 */
type InstallmentItemWithProperty = InstallmentItem & {
    propertyId: string;
    propertyName: string;
    /** Same profile unit that supplies `propertyName` (joined via sales order). */
    isOnlinePaymentEnabled: boolean;
};

/** Unit name shown on a payment row — profile unit matched to this installment. */
function propertyDisplayName(property: Property): string {
    return property.unitNumber || property.projectName || 'Unknown Property';
}

function withPropertyContext(
    property: Property,
    item: InstallmentItem,
): InstallmentItemWithProperty {
    return {
        ...item,
        propertyId: property.id,
        propertyName: propertyDisplayName(property),
        isOnlinePaymentEnabled: property.isOnlinePaymentEnabled === true,
    };
}

/** Installment ids whose profile unit allows online Pay Now. */
export function selectOnlinePaymentIds(
    items: readonly { id: string; isOnlinePaymentEnabled?: boolean }[],
): string[] {
    return items.filter((item) => item.isOnlinePaymentEnabled === true).map((item) => item.id);
}

/**
 * Get all overdue installments across all properties
 */
export function getAllOverdueInstallments(
    unitInstallments: UnitInstallmentsMap,
    properties: Property[]
): InstallmentItemWithProperty[] {
    const result: InstallmentItemWithProperty[] = [];

    properties.forEach((property) => {
        const installments = unitInstallments[property.id];
        if (installments?.overdue) {
            installments.overdue.forEach((item) => {
                result.push(withPropertyContext(property, item));
            });
        }
    });

    return result;
}

/**
 * Get all upcoming due installments across all properties
 */
export function getAllUpcomingDueInstallments(
    unitInstallments: UnitInstallmentsMap,
    properties: Property[]
): InstallmentItemWithProperty[] {
    const result: InstallmentItemWithProperty[] = [];

    properties.forEach((property) => {
        const installments = unitInstallments[property.id];
        if (installments?.upcomingDue) {
            installments.upcomingDue.forEach((item) => {
                result.push(withPropertyContext(property, item));
            });
        }
    });

    return result;
}

/**
 * Get all paid installments across all properties (for payment updates)
 */
export function getAllPaidInstallments(
    unitInstallments: UnitInstallmentsMap,
    properties: Property[],
    limit: number = 10
): InstallmentItemWithProperty[] {
    const result: InstallmentItemWithProperty[] = [];

    properties.forEach((property) => {
        const installments = unitInstallments[property.id];
        if (installments?.paid) {
            installments.paid.forEach((item) => {
                result.push(withPropertyContext(property, item));
            });
        }
    });

    // Sort by due_date descending (most recent first) and limit
    return result
        .sort((a, b) => new Date(b.due_date).getTime() - new Date(a.due_date).getTime())
        .slice(0, limit);
}

/**
 * Get the most recent paid installment across all properties
 * Returns the most recent paid installment with property information
 */
export function getMostRecentPaidInstallment(
    unitInstallments: UnitInstallmentsMap,
    properties: Property[],
    paymentType?: 'installment' | 'other_charge'
): (InstallmentItemWithProperty & { paymentDate: string }) | null {
    const allPaid: InstallmentItemWithProperty[] = [];

    properties.forEach((property) => {
        const installments = unitInstallments[property.id];
        if (!installments?.paid) return;

        installments.paid.forEach((item) => {
            // Filter by payment type if specified
            if (paymentType && item.payment_type !== paymentType) return;

            allPaid.push(withPropertyContext(property, item));
        });
    });

    if (allPaid.length === 0) return null;

    // Sort by due_date descending (most recent first) and get the first one
    const sorted = allPaid.sort(
        (a, b) => new Date(b.due_date).getTime() - new Date(a.due_date).getTime()
    );

    const mostRecent = sorted[0];
    if (!mostRecent) return null;

    // Get payment date from paid_amount date or use due_date as fallback
    // Note: API might not provide payment date, so we use due_date as the best approximation
    return {
        ...mostRecent,
        paymentDate: mostRecent.due_date, // Use due_date as payment date approximation
    } as InstallmentItemWithProperty & { paymentDate: string };
}

/**
 * Get installments for a specific property
 */
export function getPropertyInstallments(
    unitInstallments: UnitInstallmentsMap,
    propertyId: string
): UnitInstallmentsResponse | null {
    return unitInstallments[propertyId] || null;
}

/**
 * Calculate total counts across all properties
 * @deprecated Use getTotalPropertyPaymentCounts or getTotalServiceChargeCounts instead
 */
export function getTotalInstallmentCounts(
    unitInstallments: UnitInstallmentsMap
): {
    overdue: number;
    upcomingDue: number;
    future: number;
    paid: number;
    total: number;
} {
    let overdue = 0;
    let upcomingDue = 0;
    let future = 0;
    let paid = 0;

    Object.values(unitInstallments).forEach((installments) => {
        overdue += installments.counts.overdue;
        upcomingDue += installments.counts.upcomingDue;
        future += installments.counts.future;
        paid += installments.counts.paid;
    });

    return {
        overdue,
        upcomingDue,
        future,
        paid,
        total: overdue + upcomingDue + future + paid,
    };
}

/**
 * Calculate total counts for property payments (installment type only) across all properties
 */
export function getTotalPropertyPaymentCounts(
    unitInstallments: UnitInstallmentsMap,
    properties: Property[]
): {
    overdue: number;
    upcomingDue: number;
    future: number;
    paid: number;
    total: number;
} {
    let overdue = 0;
    let upcomingDue = 0;
    let future = 0;
    let paid = 0;

    properties.forEach((property) => {
        const installments = unitInstallments[property.id];
        if (!installments) return;

        // Count only installment type items
        overdue += installments.overdue.filter((item) => item.payment_type === 'installment').length;
        upcomingDue += installments.upcomingDue.filter((item) => item.payment_type === 'installment').length;
        future += installments.future.filter((item) => item.payment_type === 'installment').length;
        paid += installments.paid.filter((item) => item.payment_type === 'installment').length;
    });

    return {
        overdue,
        upcomingDue,
        future,
        paid,
        total: overdue + upcomingDue + future + paid,
    };
}

/**
 * Calculate total counts for service charges (other_charge type only) across all properties
 */
export function getTotalServiceChargeCounts(
    unitInstallments: UnitInstallmentsMap,
    properties: Property[]
): {
    overdue: number;
    upcomingDue: number;
    future: number;
    paid: number;
    total: number;
} {
    let overdue = 0;
    let upcomingDue = 0;
    let future = 0;
    let paid = 0;

    properties.forEach((property) => {
        const installments = unitInstallments[property.id];
        if (!installments) return;

        // Count only other_charge type items
        overdue += installments.overdue.filter((item) => item.payment_type === 'other_charge').length;
        upcomingDue += installments.upcomingDue.filter((item) => item.payment_type === 'other_charge').length;
        future += installments.future.filter((item) => item.payment_type === 'other_charge').length;
        paid += installments.paid.filter((item) => item.payment_type === 'other_charge').length;
    });

    return {
        overdue,
        upcomingDue,
        future,
        paid,
        total: overdue + upcomingDue + future + paid,
    };
}

/**
 * Calculate total outstanding amount for property payments (installment type only) across all properties
 */
export function getTotalPropertyPaymentOutstanding(
    unitInstallments: UnitInstallmentsMap,
    properties: Property[]
): number {
    let total = 0;

    properties.forEach((property) => {
        const installments = unitInstallments[property.id];
        if (!installments) return;

        // Get all installments (overdue, upcomingDue, future) that are installment type
        const allOutstanding = [
            ...installments.overdue,
            ...installments.upcomingDue,
            ...installments.future,
        ];

        const propertyPayments = allOutstanding.filter((item) => item.payment_type === 'installment');
        const propertyPaymentTotal = propertyPayments.reduce((sum, item) => {
            return sum + (item.due_amount || 0);
        }, 0);

        total += propertyPaymentTotal;
    });

    return total;
}

/**
 * Calculate outstanding amount for a property from installments
 */
export function calculatePropertyOutstanding(
    installments: UnitInstallmentsResponse | null
): number {
    if (!installments) return 0;

    // For items that need payment, use due_amount directly
    const overdueAmount = installments.overdue.reduce((sum, item) => {
        return sum + (item.due_amount || 0);
    }, 0);

    const upcomingAmount = installments.upcomingDue.reduce((sum, item) => {
        return sum + (item.due_amount || 0);
    }, 0);

    const futureAmount = installments.future.reduce((sum, item) => {
        return sum + (item.due_amount || 0);
    }, 0);

    return overdueAmount + upcomingAmount + futureAmount;
}

/**
 * Calculate total outstanding amount for service charges (other_charge type) across all properties
 */
export function getTotalServiceChargeOutstanding(
    unitInstallments: UnitInstallmentsMap,
    properties: Property[]
): number {
    let total = 0;

    properties.forEach((property) => {
        const installments = unitInstallments[property.id];
        if (!installments) return;

        // Get all installments (overdue, upcomingDue, future) that are other_charge type
        const allOutstanding = [
            ...installments.overdue,
            ...installments.upcomingDue,
            ...installments.future,
        ];

        const serviceCharges = allOutstanding.filter((item) => item.payment_type === 'other_charge');
        const serviceChargeTotal = serviceCharges.reduce((sum, item) => {
            return sum + (item.due_amount || 0);
        }, 0);

        total += serviceChargeTotal;
    });

    return total;
}

/**
 * Property with cheque information
 */
type PropertyWithCheques = {
    propertyId: string;
    propertyName: string;
    chequeCount: number;
    totalAmount: number;
};

/**
 * Get properties with cheques available
 */
export function getPropertiesWithCheques(
    unitInstallments: UnitInstallmentsMap,
    properties: Property[]
): PropertyWithCheques[] {
    const result: PropertyWithCheques[] = [];

    properties.forEach((property) => {
        const installments = unitInstallments[property.id];
        if (installments) {
            // Count installments with has_cheque = true
            const allInstallments = [
                ...installments.overdue,
                ...installments.upcomingDue,
                ...installments.future,
                ...installments.paid,
            ];

            const cheques = allInstallments.filter((item) => item.has_cheque);
            if (cheques.length > 0) {
                const totalAmount = cheques.reduce((sum, item) => sum + (item.paid_amount || item.amount), 0);
                result.push({
                    propertyId: property.id,
                    propertyName: property.unitNumber || property.projectName || 'Unknown Property',
                    chequeCount: cheques.length,
                    totalAmount,
                });
            }
        }
    });

    return result;
}

/**
 * Map installment status to PaymentItem status
 */
export function mapInstallmentStatusToPaymentStatus(
    category: InstallmentItem['category'],
    status: string
): 'paid' | 'missed' | 'due-soon' | 'in-process' | 'future' {
    if (category === 'paid') {
        // Check if payment is in process (paid_amount < due_amount)
        return 'paid';
    }
    if (category === 'overdue') {
        return 'missed';
    }
    if (category === 'upcomingDue') {
        return 'due-soon';
    }
    if (category === 'future') {
        return 'future';
    }
    // Default fallback
    return 'due-soon';
}

/**
 * Format date from API format (YYYY-MM-DD) to display format (DD/MM/YYYY)
 */
export function formatDueDate(dateString: string): string {
    try {
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    } catch {
        return dateString;
    }
}

/**
 * Format due date with "Due by" prefix
 * Note: This function doesn't use translations directly as it's a utility function.
 * The calling component should handle translations if needed.
 * For now, we'll keep it simple and return the formatted string.
 * If translations are needed, pass the translation function as a parameter.
 */
export function formatDueDateWithPrefix(dateString: string, dueByPrefix: string = 'Due by'): string {
    return `${dueByPrefix} ${formatDueDate(dateString)}`;
}

/**
 * Find an installment item by ID across all properties
 */
export function findInstallmentById(
    unitInstallments: UnitInstallmentsMap,
    properties: Property[],
    installmentId: string
): InstallmentItemWithProperty | null {
    for (const property of properties) {
        const installments = unitInstallments[property.id];
        if (!installments) continue;

        // Search in all categories
        const allItems = [
            ...installments.overdue.map(item => ({ ...item, category: 'overdue' as const })),
            ...installments.upcomingDue.map(item => ({ ...item, category: 'upcomingDue' as const })),
            ...installments.future.map(item => ({ ...item, category: 'future' as const })),
            ...installments.paid.map(item => ({ ...item, category: 'paid' as const })),
        ];

        const found = allItems.find(item => item.id === installmentId);
        if (found) {
            return withPropertyContext(property, found);
        }
    }

    return null;
}

/**
 * Calculate upcoming tab badge info based on priority:
 * 1. If missed payments exist → show missed count in red
 * 2. If no missed but due-soon exist → show due-soon count in yellow
 * 3. If only future payments → no badge
 */
export function getUpcomingTabBadgeInfo(
    missedCount: number,
    dueSoonCount: number,
    redColor: string,
    yellowColor: string
): {
    count: number;
    color: string;
    show: boolean;
} {
    if (missedCount > 0) {
        return {
            count: missedCount,
            color: redColor,
            show: true,
        };
    } else if (dueSoonCount > 0) {
        return {
            count: dueSoonCount,
            color: yellowColor,
            show: true,
        };
    } else {
        // Only future payments (no missed, no due-soon) → no badge
        return {
            count: 0,
            color: redColor, // Default color (not used when show is false)
            show: false,
        };
    }
}

